import React, { useContext, useEffect, useState } from "react";

// Import reactstrap components
import {
    Button,
    Card,
    CardHeader,
    CardBody,
    Form,
    Input,
    Container,
    Row,
    Col,
    FormGroup,
    Modal,
    Label,
} from "reactstrap";

// Core components
import SimpleHeader from "components/Headers/SimpleHeader";
import RegisterTable from "components/Tables/RegisterTable";
import { CurrentUserContext } from "containers/App";
// Import API services
import {
    getAllAreas,
    getAllUsers,
    addNewUser,
    updateUser,
    getAllUserRoles,
} from "services/admin";

// Constants
const usersTableMapping = {
    username: { tableHeading: 'Usuario' },
    firstname: { tableHeading: 'Nombres' },
    lastname: { tableHeading: 'Apellidos' },
    email: { tableHeading: 'E-mail' },
    area_id: { tableHeading: 'Id del area' },
    area_name: { tableHeading: 'Area' },
    position: { tableHeading: 'Cargo' },
    role_id: { tableHeading: 'Id del rol' },
    role_name: { tableHeading: 'Rol' },
    is_active: { tableHeading: 'Activo' },
}

const emptyUserForm = {
    username: '',
    firstname: '',
    lastname: '',
    email: null,
    area_id: null,
    position: null,
    role_id: null,
};

const UsersSettings = () => {
    // Declare states
    const [users, setUsers] = useState([]);
    const [modifiedUsers, setModifiedUsers] = useState([]);
    const [newUser, setNewUser] = useState(emptyUserForm);
    const [columnKeys, setColumnKeys] = useState([]);
    // Complementary data
    const [areas, setAreas] = useState([]);
    const [roles, setRoles] = useState([]);
    const [editingUser, setEditingUser] = useState(null);
    // Only fields the user changed while editing (partial PATCH payload)
    const [updatedData, setUpdatedData] = useState({});
    const [saving, setSaving] = useState(false);

    const { currentUser, setCurrentUser } = useContext(CurrentUserContext);

    useEffect(() => {
        // Fetch users from server at first render
        fetchUsers();
        // Fetch complementary data
        fetchAreas();
        fetchRoles();
    }, []);

    useEffect(() => {
        // Add a 'area_name' property to each user record
        let modifiedUsersList = users.map(user => {
            return({
                ...user,
                area_name: user.area ? user.area.name : null,
                role_name: user.role ? user.role.name : null
            })
        });
        setModifiedUsers(modifiedUsersList);

    }, [users])

    // Utility functions
    
    // Converts empty strings to null; parses area_id and role_id as numbers if set.
    const normalizeUserInputValue = (name, value) => {
        if (name === 'area_id' || name === 'role_id') {
            return value === '' ? null : Number(value);
        }
        if (value === '') {
            return null;
        }
        return value;
    };

    const fetchUsers = () => {
        getAllUsers()
            .then((data) => {
                if (data.success) {
                    setUsers(data.users);
                    // Get column keys that must be displayed in the table
                    let colKeys = data.table.column_keys;
                    setColumnKeys(colKeys);
                }
            });
    }
    const fetchAreas = () => {
        getAllAreas()
            .then(data => {
                if (data.success) {
                    setAreas(data.areas);
                }
            });
    }
    const fetchRoles = () => {
        getAllUserRoles()
            .then(data => {
                if (data.success) {
                    setRoles(data.roles);
                }
            })
    }

    // Handlers
    const handleCreateInputsChange = (e) => {
        const { name, value } = e.target;
        setNewUser((prev) => {
            const next = { ...prev, [name]: normalizeUserInputValue(name, value) };
            // Reset position when area changes because position depends on the area.
            if (name === 'area_id') {
                next.position = null;
            }
            return next;
        });
    }

    const handleSubmitNew = (e) => {
        e.preventDefault();
        // newUser is already API-shaped (null clears, IDs as numbers)
        addNewUser(newUser)
            .then((data) => {
                if (data.success) {
                        console.log(data.message);
                        setNewUser(emptyUserForm);
                        // Fetch users from server to update table
                        fetchUsers();
                    } else {
                        alert(data.message || 'Ocurrió un error.'); //TODO: Improve error handling by showing specific error message from the server in spanish.
                    }
                })
            .catch(error => alert('Ocurrió un error.'));
    }

    const handleOpenModalEdit = (e, userId) => {
        e.preventDefault();
        const user = users.find((u) => u.id === userId);
        if (!user) {
            console.error('User not found');
            return;
        }
        // Clear updatedData
        setUpdatedData({});
        setEditingUser(user);
    };

    const closeEditModal = () => {
        setEditingUser(null);
        setUpdatedData({});
    };

    const handleEditInputsChange = (e) => {
        const { name, value } = e.target;
        setUpdatedData((prev) => {
            const next = { ...prev, [name]: normalizeUserInputValue(name, value) };
            // Reset position when area changes because position depends on the area.
            if (name === 'area_id') {
                next.position = null;
            }
            return next;
        });
    };

    const handleUpdateRecord = () => {
        // Perform data validations before updating user
        if (Object.keys(updatedData).length === 0) {
            closeEditModal();
            return;
        }
        if (updatedData.hasOwnProperty('username') && (!updatedData.username || updatedData.username.length < 5)) {
            alert('El usuario debe tener al menos 5 caracteres.');
            return;
        }
        if (updatedData.hasOwnProperty('firstname') && !updatedData.firstname) {
            alert('Ingrese nombres.');
            return;
        }
        if (updatedData.hasOwnProperty('lastname') && !updatedData.lastname) {
            alert('Ingrese apellidos.');
            return;
        }
        setSaving(true);
        // Update user
        updateUser(editingUser.id, updatedData)
            .then((data) => {
                if (data.success) {
                    // If the updated user is the current user, update the current user context
                    if (currentUser && editingUser.id === currentUser.id) {
                        setCurrentUser({ ...currentUser, ...data.user });
                    }
                    // Close edit modal and fetch users to update table
                    closeEditModal();
                    fetchUsers();
                } else {
                    alert(data.message ||'Ocurrió un error.'); //TODO: Improve error handling by showing specific error message from the server in spanish.
                }
            })
            .catch(() => alert('Ocurrió un error.'))
            .finally(() => setSaving(false));
    };

    const handleToggleActive = (userId, isActive) => {
        updateUser(userId, { is_active: isActive })
            .then((data) => {
                if (data.success) {
                    fetchUsers();
                } else {
                    alert(data.message ||'Ocurrió un error.'); //TODO: Improve error handling by showing specific error message from the server in spanish.
                }
            })
            .catch(() => alert('Ocurrió un error.'));
    };

    // Table customization variables
    const headerButtons = [
        { className: 'btn-neutral', color: 'default', content: 'Nuevo', link: '#add-new' },
    ];

    const inlineActions = [
        { text: 'Editar', handler: handleOpenModalEdit, property: 'id', icon: 'fas fa-edit' },
    ];

    const columnRenderers = {
        is_active: (record) => {
            const isCurrentUser = currentUser && record.id === currentUser.id;
            return (
                <label className="custom-toggle">
                    <input
                        type="checkbox"
                        checked={Boolean(record.is_active)}
                        disabled={isCurrentUser}
                        onChange={(e) => handleToggleActive(record.id, e.target.checked)}
                    />
                    <span
                        className="custom-toggle-slider rounded-circle"
                        data-label-off="No"
                        data-label-on="Sí"
                    />
                </label>
            );
        },
    };

    // Render component
    return (
        <>
        <SimpleHeader
            name="Usuarios"
            parentName="Bases de datos"
            buttons={headerButtons}
        />
        <Container className="mt--6" fluid>
            {/* Render table to display all users */}
            <RegisterTable
                columnKeys={columnKeys}
                records={modifiedUsers}
                tableMapping={usersTableMapping}
                tableTitle="Registro de Usuarios"
                inlineActions={inlineActions}
                columnRenderers={columnRenderers}
            />
            {/* Render form for adding new users */}
            <Card className="mb-4">
                <CardHeader>
                    <h3>Registrar Nuevo Usuario</h3>
                </CardHeader>
                <CardBody>
                    <UsersForm
                        formType="create"
                        formData={newUser}
                        onChange={handleCreateInputsChange}
                        onSubmit={handleSubmitNew}
                        areas={areas}
                        roles={roles}
                    />
                </CardBody>
            </Card>
        </Container>

        <Modal
            className="modal-dialog-centered"
            isOpen={Boolean(editingUser)}
            toggle={closeEditModal}
            size="lg"
        >
            <div className="modal-header">
                <h3 className="modal-title">Editar usuario</h3>
                <button
                    aria-label="Close"
                    className="close"
                    data-dismiss="modal"
                    type="button"
                    onClick={closeEditModal}
                >
                    <span aria-hidden={true}>×</span>
                </button>
            </div>
            <div className="modal-body">
                <p>
                    Puedes modificar el siguiente <strong>Usuario</strong>:
                </p>
                <UsersForm
                    formType="edit"
                    defaultData={editingUser}
                    updatedData={updatedData}
                    onChange={handleEditInputsChange}
                    areas={areas}
                    roles={roles}
                />
            </div>
            <div className="modal-footer">
                <Button
                    color="info"
                    type="button"
                    onClick={handleUpdateRecord}
                    disabled={saving}
                >
                    {saving ? 'Guardando…' : 'Guardar'}
                </Button>
                <Button
                    className="ml-auto"
                    color="link"
                    type="button"
                    onClick={closeEditModal}
                    disabled={saving}
                >
                    Cancelar
                </Button>
            </div>
        </Modal>
        </>
    );
}

/**
 * Shared create/edit user fields (same layout; labels on both flows).
 * Create: controlled formData. 
 * Edit: defaultData + updatedData (only changed fields).
 */
const UsersForm = ({
    formType,
    formData,
    defaultData,
    updatedData = {},
    onChange: handleChange,
    onSubmit: handleSubmit,
    areas,
    roles,
}) => {
    // Resolve displayed value for a field
    const fieldValue = (name, fallback = '') => {
        // Use formData for create (null means empty input)
        if (formType === 'create') {
            const created = formData?.[name];
            if (created == null || created === '') {
                return fallback;
            }
            return String(created);
        }
        // Use updatedData for edit, if it exists (null means cleared → show empty input)
        if (updatedData.hasOwnProperty(name)) {
            const updated = updatedData[name];
            if (updated == null) {
                return fallback;
            }
            return String(updated);
        }
        // Use default data
        const raw = defaultData?.[name];
        if (raw == null) {
            return fallback;
        }
        return String(raw);
    };

    const areaId = fieldValue('area_id');
    const area = areas.find((a) => a.id === Number(areaId));
    const positions = area?.positions || [];

    return (
        <Form
            onSubmit={(e) => {
                e.preventDefault();
                if (handleSubmit) {
                    handleSubmit(e);
                }
            }}
        >
            <Row>
                <Col sm="6">
                    <FormGroup>
                        <Label className="form-control-label" for={`username-${formType}`}>
                            Usuario
                        </Label>
                        <Input
                            id={`username-${formType}`}
                            placeholder="Usuario"
                            type="text"
                            name="username"
                            value={fieldValue('username')}
                            onChange={handleChange}
                        />
                    </FormGroup>
                </Col>
                <Col sm="6">
                    <FormGroup>
                        <Label className="form-control-label" for={`email-${formType}`}>
                            E-mail
                        </Label>
                        <Input
                            id={`email-${formType}`}
                            placeholder="E-mail (opcional)"
                            type="email"
                            name="email"
                            value={fieldValue('email')}
                            onChange={handleChange}
                        />
                    </FormGroup>
                </Col>
            </Row>
            <Row>
                <Col sm="6">
                    <FormGroup>
                        <Label className="form-control-label" for={`firstname-${formType}`}>
                            Nombre(s)
                        </Label>
                        <Input
                            id={`firstname-${formType}`}
                            placeholder="Nombre(s)"
                            type="text"
                            name="firstname"
                            value={fieldValue('firstname')}
                            onChange={handleChange}
                        />
                    </FormGroup>
                </Col>
                <Col sm="6">
                    <FormGroup>
                        <Label className="form-control-label" for={`lastname-${formType}`}>
                            Apellido(s)
                        </Label>
                        <Input
                            id={`lastname-${formType}`}
                            placeholder="Apellido(s)"
                            type="text"
                            name="lastname"
                            value={fieldValue('lastname')}
                            onChange={handleChange}
                        />
                    </FormGroup>
                </Col>
            </Row>
            <Row>
                <Col sm="6">
                    <FormGroup>
                        <Label className="form-control-label" for={`area_id-${formType}`}>
                            Area
                        </Label>
                        <Input
                            id={`area_id-${formType}`}
                            type="select"
                            name="area_id"
                            value={areaId}
                            onChange={handleChange}
                        >
                            <option value="">Selecciona el area</option>
                            {areas.map((areaOption) => (
                                <option value={areaOption.id} key={areaOption.id}>
                                    {areaOption.name}
                                </option>
                            ))}
                        </Input>
                    </FormGroup>
                </Col>
                <Col sm="6">
                    <FormGroup>
                        <Label className="form-control-label" for={`position-${formType}`}>
                            Cargo
                        </Label>
                        <Input
                            id={`position-${formType}`}
                            type="select"
                            name="position"
                            value={fieldValue('position')}
                            onChange={handleChange}
                        >
                            <option value="">Selecciona el cargo</option>
                            {positions.map((position) => (
                                <option key={position} value={position}>
                                    {position}
                                </option>
                            ))}
                        </Input>
                    </FormGroup>
                </Col>
            </Row>
            <Row>
                <Col sm="6">
                    <FormGroup>
                        <Label className="form-control-label" for={`role_id-${formType}`}>
                            Rol
                        </Label>
                        <Input
                            id={`role_id-${formType}`}
                            type="select"
                            name="role_id"
                            value={fieldValue('role_id')}
                            onChange={handleChange}
                        >
                            <option value="">Selecciona el rol</option>
                            {roles.map((role) => (
                                <option value={role.id} key={role.id}>
                                    {role.name}
                                </option>
                            ))}
                        </Input>
                    </FormGroup>
                </Col>
            </Row>
            {formType === 'create' ? (
                <div className="text-left">
                    <Button color="primary" type="submit">
                        Registrar
                    </Button>
                </div>
            ) : null}
        </Form>
    );
};

export default UsersSettings;
